import { Component, Input, Output, EventEmitter, OnInit, Inject, OnChanges } from 'ng-metadata/core';
import './app-container.component.scss';
import { AppContainerStateService } from './app-container-state.service';
import { AppContainerStateChangeName } from './shared/shared.interfaces';

/*
 * *******************************************************************************  WARNING  ***************************************************************************************
 * This component is deprecated in favor of giving user the freedom of composing general app container freely with navigation, account-menu, user-menu and other components in this
 * library.
 * *********************************************************************************************************************************************************************************
 */

/**
 * @ngdoc directive
 * @name mwAppContainer
 * @module mw.components.appContainer
 *
 * @param {string} [header-text='Meltwater'] - The title to be displayed in the header.
 * @param {string} [theme='default'] - The name of the angular material theme to use.
 * @param {array} [nav-items] - The list of application navigaton items to present to the user in the left navigation bar.
 * @param {array} [tab-items] - The list of application tab items to present to the user in the application tab bar.
 * @param {array} [accounts] - The list of accounts (0-*) to present to the user allowing them to switch between them.
 * @param {object} [user] - The User object used to display in the `<mw-user-menu>`.
 * @param {object} [init-selected-accounts] - The initially selected account to use from the list of accounts provided.
 * @param {boolean} [hide-chrome=false] - Default false.  Dictates whether the "application chrome" (header, nav) is hidden, which is used for some cases like full page modals.
 * @param {boolean} [hide-help=false] - Default false.  Dictates whether the Help Center icon and button should be hidden.
 * @param {string} [user-menu-main-header-url] - The URL that will be followed on click of the user's email that is shown in the `<mw-user-menu>`.
 *
 * @param {expression} [on-account-select] - Angular expression to evaluate when an account is selected from the account menu. Event
 *     object is available as `$event`, and contains the selected account object.
 * @param {expression} [on-nav-item-select] - Angular expression to evaluate when a navigation item is selected. Event
 *     object is available as `$event`, and contains the selected navigation item object.
 * @param {expression} [on-tab-item-select] - Angular expression to evaluate when a tab item is selected. Event
 *     object is available as `$event`, and contains the selected tab item object.
 * @param {expression} [on-help] - Angular expression to evaluate when the Help Center button is clicked.
 * @param {expression} [on-logout] - Angular expression to evaluate when the Logout button is clicked (which is in the `<mw-user-menu>`).
 * @param {expression} [on-nav-resize] - Angular expression to evaluate when the navigation menu is toggled between maximized/minimized.  Note, this event is NOT fired when the navigation menu goes under a "hamburger" button for 'xs' and 'sm' screen sizes.
 * @param {expression} [on-user-menu-main-header-click] - Angular expression to evaluate on click of the user's email that is shown in the `<mw-user-menu>`.
 *
 * @description
 *
 * WARNING: This component is depricated in favor of giving user the freedom of composing general app container freely with `<mw-account-selector>`, `<mw-user-menu>`, `<mw-navigation>` and other components in this
 * library.
 *
 * `<md-app-container>` is a component used display an application container with header, navigation and outlet for route rendering.
 * It uses `<mw-account-selector>` `<mw-user-menu>` and `<mw-navigation>` internally for the header and navigation components.  See the
 * documentation for those components for more details.
 *
 * @usage
 * <hljs hljs-language="html">
 *   <mw-app-container header-text="My App" nav-items="$ctrl.navItems" accounts="$ctrl.accounts">
 *      <mw-secondary-navigation-slot>
 *      </mw-secondary-navigation-slot>
 *      <mw-content-header-slot>
 *      <!--Slot for content that should be positioned above the scrollable main content area, empty by default-->
 *      </mw-content-header-slot>
 *      <mw-content-slot>
 *      <!--Slot for main content, empty by default-->
 *      </mw-content-slot>
 *   </mw-app-container>
 * </hljs>
 *
 */
@Component({
    selector: 'mw-app-container',
    template: `
        <div class="mw-app-container" ng-class="['mw-app-container--'+$ctrl.getScreenSize()]" md-theme="{{$ctrl.theme}}" layout="row" ng-cloak="ng-cloak">
            <mw-navigation class="mw-app-container__navigation ng-animate-disabled" ng-hide="$ctrl.hideChrome" ng-class="{'expanded': $ctrl.isSideNavMaximized, 'xs-sm': $ctrl.getScreenSize() === 'xs' || $ctrl.getScreenSize() === 'sm'}" items="$ctrl.navItems" on-header-click="$ctrl.navHeaderClick($event)" on-select="$ctrl.selectNavItem($event)" on-minimize="$ctrl.handleNavResize(false)" on-maximize="$ctrl.handleNavResize(true)"></mw-navigation>
            <section class="mw-app-container__main relative" layout="column" ng-class="{'expanded': $ctrl.isSideNavMaximized}" layout-fill="layout-fill" role="main">
                <!-- Header Bar-->
                <md-toolbar class="md-whiteframe-z1 ng-animate-disabled" ng-hide="$ctrl.hideChrome">
                    <div class="mw-app-container__header md-toolbar-tools">
                        <md-button class="mw-app-container__header-hamburger md-icon-button" hide-gt-sm md-no-ink aria-label="Menu" ng-click="$ctrl.showNav()">
                            <md-icon md-svg-icon="navigation:ic_menu_24px"></md-icon>
                        </md-button>
                        <!-- Header Title-->
                        <span class="mw-app-container__header-title mw-app-header__title" translate="{{$ctrl.headerText}}"></span>
                        <span ng-if="$ctrl.hasSecondaryNavSlot()" hide-xs><span class="mw-app-container__header-separator" hide-xs>|</span></span>
                        <span ng-transclude="secondaryNavSlot" hide-xs></span>
                        <div flex="auto"></div>
                        <mw-account-menu id="mw-account-menu" accounts="$ctrl.accounts" init-selected-account="$ctrl.initSelectedAccount" on-select="$ctrl.selectAccount($event)"></mw-account-menu>

                        <md-button aria-label="{{'Help' | translate}" ng-click="$ctrl.help($event)" ng-if="!$ctrl.hideHelp && $ctrl.shouldUseHelpButton === false" md-no-ink class="mw-app-container__help md-icon-button" hide-xs>
                            <md-tooltip md-direction="bottom" md-delay="2000">
                                <span translate="Help" translate-default="Help"></span>
                            </md-tooltip>
                            <md-icon class="mw-app-container__help-icon" md-svg-icon="meltwater:helpcenter"></md-icon>
                        </md-button>

                        <md-button id="mw-app-header__help-button" ng-if="!$ctrl.hideHelp &&$ctrl.shouldUseHelpButton === true" aria-label="{{'Help' | translate}" ng-click="$ctrl.help($event)" class="mw-button mw-app-header__help-button" hide-xs>
                            <span translate>{{$ctrl.helpButtonText}}</span>
                        </md-button>

                        <div class="mw-app-header__notifications" ng-transclude="notificationsSlot"></div>                     

                        <mw-user-menu id="mw-user-menu" class="mw-app-container__user-menu" user="$ctrl.user" main-header-url="{{$ctrl.userMenuMainHeaderUrl}}" logout-url="{{$ctrl.logoutUrl}}" hide-help="$ctrl.hideHelp" on-help="$ctrl.help($event)" on-logout="$ctrl.logout($event)"></mw-user-menu>
                    </div>
                </md-toolbar>
                <md-tabs class="mw-app-container__tabs-navigation ng-animate-disabled" md-no-ink ng-hide="$ctrl.hideChrome || !$ctrl.hasTabItems()" md-autoselect="false">
                    <md-tab ng-repeat="item in $ctrl.tabItems track by $id(item)" md-active="item.isActive">
                        <md-tab-label><div ng-click="$ctrl.selectTabItem(item, $event)" translate="{{item.title}}">{{item.title}}</div></md-tab-label>
                    </md-tab>
                </md-tabs>
                <!-- Main Content Header (Optional Slot) -->
                <div class="mw-app-container__content-header" ng-transclude="contentHeaderSlot">
                    <!--Slot for content that should be positioned above the scrollable main content area, empty by default-->
                </div>

                <!-- Main Content -->
                <md-content class="mw-app-container__content" flex ng-transclude="contentSlot">
                    <!--Slot for main content, empty by default-->
                </md-content>
            </section>
        </div>
    `,
    legacy: {
        transclude: {
            'contentSlot': '?mwContentSlot',
            'contentHeaderSlot': '?mwContentHeaderSlot',
            'secondaryNavSlot': '?mwSecondaryNavigationSlot',
            'notificationsSlot': '?mwNotificationsSlot',
        }
    }
})
export class AppContainerComponent implements OnInit, OnChanges {
    @Input('<') accounts: any[] = [];
    @Input('@') headerText: string;
    @Input('<') initSelectedAccount: any = null;
    @Input('<') hideChrome: boolean = false;
    @Input('<') hideHelp: boolean = false;
    @Input('<') navItems: any[] = [];
    @Input('<') tabItems: any[] = [];
    @Input('<') theme: string = 'default';
    @Input('<') user: any = null;
    @Input('@') userMenuMainHeaderUrl: string = '';

    @Output() onAccountSelect = new EventEmitter<Object>();
    @Output() onHelp = new EventEmitter<ng.IAngularEvent>();
    @Output() onLogout = new EventEmitter<ng.IAngularEvent>();
    @Output() onNavHeaderClick = new EventEmitter<ng.IAngularEvent>();
    @Output() onNavItemSelect = new EventEmitter<Object>();
    @Output() onNavResize = new EventEmitter<boolean>();
    @Output() onTabItemSelect = new EventEmitter<Object>();

    private title;
    private isSideNavMaximized: boolean = false;
    private selectedTabItem: any = null;
    private shouldUseHelpButton: boolean = false;
    private helpButtonText: string = 'Help';

    constructor(@Inject('$mdMedia') private $mdMedia: ng.material.IMedia,
                @Inject('$mdSidenav') private $mdSidenav: ng.material.ISidenavService,
                @Inject('$transclude') private $transclude: any,
                @Inject('$cookies') private $cookies: ng.cookies.ICookiesService,
                @Inject('mwAppContainerStateService') private mwAppContainerStateService: AppContainerStateService,
                @Inject('$rootScope') private $rootScope: ng.IRootScopeService,
                @Inject('$translate') private $translate: ng.translate.ITranslateService,
                @Inject('$window') private $window: ng.IWindowService) {
    }

    ngOnInit() {
        if (this.$window.mwUseHelpButton) {
            this.shouldUseHelpButton = this.$window.mwUseHelpButton;
        }

        this.isSideNavMaximized = this.mwAppContainerStateService.getIsNavigationMinimized();
        this.$rootScope.$on('$translateChangeSuccess', () => {
            if (this.shouldUseHelpButton) {
                this.updateHelpTextState();
            }
            if (this.headerText && this.headerText.length) {
                this.updatePageNameState();
            }
        });
    }

    ngOnChanges(changes) {
        if (changes.headerText && changes.headerText.currentValue && (changes.headerText.currentValue != changes.headerText.previousValue)) {
            this.updatePageNameState();
        }
    }

    updatePageNameState() {
        const translatedHeaderText = this.$translate.instant(this.headerText);
        
        this.mwAppContainerStateService.updateAppContainerState(AppContainerStateChangeName.pageName, translatedHeaderText);
    }

    updateHelpTextState() {
        const translatedHelpButtonText = this.$translate.instant(this.helpButtonText);

        this.mwAppContainerStateService.updateAppContainerState(AppContainerStateChangeName.helpText, translatedHelpButtonText);
    }

    // We should move this to a reusable service
    getScreenSize() {
        if (this.$mdMedia('xs')) { return 'xs'; }
        if (this.$mdMedia('sm')) { return 'sm'; }
        if (this.$mdMedia('md')) { return 'md'; }
        if (this.$mdMedia('lg')) { return 'lg'; }
        if (this.$mdMedia('xl')) { return 'xl'; }
    }

    selectAccount(account: Object) {
        this.onAccountSelect.emit(account);
    }

    navHeaderClick(ngClickEvent: ng.IAngularEvent) {
        this.onNavHeaderClick.emit(ngClickEvent);
    }

    selectNavItem(navItem: Object) {
        this.onNavItemSelect.emit(navItem);
    }

    selectTabItem(tabItem: Object, ngClickEvent: ng.IAngularEvent) {

        // Reset all to inactive and and then set new one be active
        for (let key in this.tabItems) {
            this.tabItems[key].isActive = false;
        }

        this.selectedTabItem = tabItem;
        this.selectedTabItem.isActive = true;

        // We stop the propagation of the click event so that the md-tabs component does not automatically make the tab active
        // Instead, we want to control the state of the active tab from outside the component
        if (ngClickEvent) {
            ngClickEvent.stopPropagation();
        }

        this.onTabItemSelect.emit(this.selectedTabItem);
    }

    logout(ngClickEvent: ng.IAngularEvent) {
        this.onLogout.emit(ngClickEvent);
    }

    showNav() {
        this.$mdSidenav('mwNavigationSideNav').toggle();
    }

    handleNavResize(maximized: boolean) {
        this.isSideNavMaximized = maximized;
        this.onNavResize.emit(this.isSideNavMaximized);
    }

    hasSecondaryNavSlot() {
        return this.$transclude.isSlotFilled('secondaryNavSlot');
    }

    hasTabItems() {
        return this.tabItems && this.tabItems.length > 0;
    }

    help(ngClickEvent: ng.IAngularEvent) {
        this.onHelp.emit(ngClickEvent);
    }
}



// WEBPACK FOOTER //
// ./src/lib/components/app-container/app-container.component.ts